"use client";

import {
  ServicePageLayout,
  ConsultationCTA,
  ServiceHeroImage,
  ServiceSection,
  ServiceGrid,
  ServiceFeatureCard,
  ServiceBenefitItem,
  ServiceInfoBox,
  ServiceStepList,
  ServiceFaq,
} from "@/components/services";
import { StructuredData } from "@/components/common";
import Link from "next/link";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";

// Breaks a band section out of the layout's centered container to full viewport width.
const FULL_BLEED = "mx-[calc(50%_-_50vw)]";

const integrationApproachSteps = [
  {
    title: "Discovery & Assessment",
    description:
      "We thoroughly analyze your existing systems, data flows, and business processes to identify integration requirements and opportunities."
  },
  {
    title: "Integration Strategy",
    description:
      "We design a comprehensive integration architecture that aligns with your business goals, considering both immediate needs and future scalability."
  },
  {
    title: "Implementation",
    description:
      "Our development team builds the integration solutions using the most appropriate technologies and methodologies for your specific scenario."
  },
  {
    title: "Testing & Validation",
    description:
      "We conduct thorough testing, including integration testing, performance testing, and data validation to ensure everything works seamlessly."
  },
  {
    title: "Deployment & Support",
    description:
      "We manage the deployment process and provide ongoing monitoring, maintenance, and support to ensure your integrated systems continue to perform optimally."
  }
];

// Diagram-style view of the systems we connect through an API/event-bus spine.
const integrationSystems = ["ERP", "CRM", "E-commerce", "Warehouse", "Accounting"];

const integrationPatternGroups = [
  {
    label: "Synchronous Patterns",
    items: [
      "Request-Response",
      "Remote Procedure Call (RPC)",
      "Service-Oriented Architecture (SOA)",
    ],
  },
  {
    label: "Asynchronous Patterns",
    items: ["Publish-Subscribe", "Event-Driven Architecture", "Message Queuing"],
  },
  {
    label: "Modern Integration Approaches",
    items: [
      "Microservices Architecture",
      "API-First Design",
      "Containerization & Orchestration",
      "Serverless Integration",
    ],
  },
  // AI-era patterns. The three groups above predate LLMs entirely, so a page
  // claiming AI integration capability while listing only ESB and ETL reads as
  // out of date to exactly the technical buyer it needs to convince.
  {
    label: "AI Integration Patterns",
    items: [
      "Model Context Protocol (MCP) servers",
      "Tool-use and function calling",
      "Retrieval-Augmented Generation (RAG) over your documents",
      "Human-in-the-loop approval gates",
      "Scoped service accounts and audit logging",
    ],
  },
];

// Success stories rendered as a dark proof moment with stat callouts.
const successStories = [
  {
    label: "Case 01 · Education logistics",
    title: "Education Logistics Integration",
    stats: [
      { value: "65%", label: "Faster order processing" },
      { value: "99.9%", label: "Inventory accuracy" },
    ],
    narrative:
      "We connected a leading education company's NetSuite ERP with their warehouse management system — automating inventory tracking, order fulfillment, and distribution with real-time stock visibility and automated reordering across multiple warehouses.",
  },
  {
    label: "Case 02 · Construction",
    title: "Construction Company AI Integration",
    stats: [
      { value: "85%", label: "Less manual entry" },
      { value: "70%", label: "Time reduction" },
      { value: "98%", label: "Extraction accuracy" },
    ],
    narrative:
      "We linked a construction firm's accounting system to OCR AI that reads invoices, receipts, and purchase orders — extracting and categorizing financial data, matching documents to projects, and updating the books automatically for faster reconciliation.",
  },
];

const sisFAQs = [
  {
    question: "What is MCP (Model Context Protocol) in business terms?",
    answer: "MCP is an open standard, published by Anthropic, that defines how an AI assistant connects to the software a business already runs — ERP, CRM, e-commerce, accounting. The business case is that it replaces bespoke, brittle connections with one agreed shape: a connector built to the standard keeps working when the AI changes, so the integration you paid for remains yours. It also keeps permissions in your systems rather than in the AI, which is what makes an AI's access to customer data reviewable under PDPA in Malaysia and PDPC in Singapore."
  },
  {
    question: "Can AI actually connect to our ERP or accounting system?",
    answer: "Yes, and this is now ordinary integration work rather than research. We connect Claude to systems including SQL Account, AutoCount, Odoo, NetSuite, Microsoft Dynamics and custom in-house applications, so staff can ask questions of live data or trigger an action that a human then approves. The honest caveat: read-only access is quick to justify, while write access to a ledger needs approval steps, logging and testing against your real data before it goes live. That governance work is usually the larger part of the project."
  },
  {
    question: "Do we need AI to fix our integration problem?",
    answer: "Often no. If the requirement is moving the same fields between two systems on a schedule, a conventional API integration is cheaper, faster and more reliable than anything involving a model — and it will not surprise you. AI earns its place when the step in the middle needs judgement: reading an unstructured supplier invoice, deciding which purchase order it matches, or noticing that something does not reconcile and escalating instead of guessing. We will tell you which of the two you actually have."
  },
  {
    question: "How much does system integration cost in Malaysia?",
    answer: "System integration projects in Malaysia typically range from RM 15,000 for a single point-to-point integration to RM 150,000+ for enterprise-wide integration across multiple systems. Anchor Sprint scopes every project after a free assessment, and we offer phased delivery so you can start with the highest-impact connection first."
  },
  {
    question: "How long does a typical integration project take?",
    answer: "A simple API integration between two systems takes 3-6 weeks. Mid-sized projects connecting ERP, CRM, and e-commerce platforms typically take 2-4 months, while complex enterprise integrations with data migration can take 4-6 months. We deliver in increments, so you see working integrations early."
  },
  {
    question: "Which ERP and business systems can you integrate?",
    answer: "We integrate NetSuite, SAP, Microsoft Dynamics, Odoo, SQL Account, AutoCount, and other systems popular with Malaysian businesses — plus CRMs like Salesforce and HubSpot, e-commerce platforms like Shopify and Shopee, warehouse management systems, and custom in-house applications via APIs."
  },
  {
    question: "How do you keep our data secure during integration?",
    answer: "We follow security best practices including encrypted data transfer over TLS, token-based API authentication, least-privilege access controls, and audit logging. For sensitive workloads we can deploy integration middleware on your own infrastructure so data never leaves your environment, supporting PDPA compliance."
  },
  {
    question: "Do you provide support after the integration goes live?",
    answer: "Yes. We provide monitoring, error alerting, and maintenance plans to keep integrations healthy as your systems evolve. When a connected platform changes its API or you add new systems, we update the integration — with SLAs available for business-critical data flows."
  }
];

// Service image from public directory
const sisImage = "/images/services/svc-system-integration.jpg";

export default function SystemIntegration() {
  const locale = useLocale();
  const translate = (text: string) => t(locale, text);

  const eventBusLabel = (
    <span className="whitespace-nowrap rounded-full border border-primary/30 bg-primary/10 px-4 py-2 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-primary">
      {translate("API / Event Bus")}
    </span>
  );

  return (
    <ServicePageLayout
      title="Business and System Integration in Malaysia"
      breadcrumb="Services / System Integration"
      currentServiceId="sis"
      dek="We are the IT system integrator Malaysian and Singaporean businesses call when their systems have stopped talking to each other. Business integration is unglamorous work — connecting ERP, CRM, e-commerce, warehouse and accounting systems so data moves between them without anyone re-keying it — and it is what decides whether the rest of your technology spend pays off. Increasingly it also means connecting AI to those same systems, through the Model Context Protocol."
    >
      <ServiceHeroImage
        src={sisImage}
        alt="System Integration and Support Services Malaysia"
      />

      <ServiceSection
        title="Integration Services We Offer"
        eyebrow="What we connect"
        accent="Integration Services"
        dek="APIs, data pipelines, and enterprise applications — joined into one coherent system."
        band="ivory"
        className={FULL_BLEED}
      >
        <ServiceGrid columns={2}>
          <ServiceFeatureCard
            icon="fas fa-exchange-alt"
            title="API Development & Management"
            description="Custom API design, development, and management to enable smooth communication between different applications and services."
          />

          <ServiceFeatureCard
            icon="fas fa-database"
            title="Data Integration"
            description="Comprehensive solutions for extracting, transforming, and loading data between diverse systems while maintaining data integrity."
          />

          <ServiceFeatureCard
            icon="fas fa-building"
            title="Enterprise Application Integration"
            description="Connect and integrate critical business applications such as CRM, ERP, HRM, and financial systems for streamlined operations."
          />

          <ServiceFeatureCard
            icon="fas fa-cloud"
            title="Cloud Integration"
            description="Seamlessly connect on-premises systems with cloud services and SaaS applications for hybrid infrastructure optimization."
          />
        </ServiceGrid>
      </ServiceSection>

      {/* AI integration via MCP. Deliberately business-language: the searches that
          bring people here are "business integration malaysia" and "it system
          integrator malaysia", not "model context protocol". The protocol is
          explained as the mechanism, never as the headline. */}
      <ServiceSection
        title="Connecting AI to the systems you already run"
        eyebrow="AI integration"
        accent="AI integration"
        dek="The newest integration problem is not connecting two databases. It is letting an AI assistant read your stock levels, raise a purchase order, or answer a customer about their invoice — safely."
        band="ivory"
        className={FULL_BLEED}
      >
        <ServiceInfoBox
          title="What the Model Context Protocol means for your business"
          description="MCP is an open standard published by Anthropic, the makers of Claude. It matters commercially rather than technically, for three reasons:"
          variant="neutral"
          className="mb-8"
        >
          <ul className="list-disc space-y-2 pl-5 text-neutral-muted">
            <li>
              <span className="font-medium">{translate("Integration work stops being throwaway.")}</span>{" "}
              {translate(
                "Before MCP, every AI-to-system connection was custom-built and rewritten whenever either side changed. A connector built to the standard is built once and keeps working.",
              )}
            </li>
            <li>
              <span className="font-medium">{translate("It reduces vendor lock-in rather than adding it.")}</span>{" "}
              {translate(
                "A connector to your ERP works with any AI that speaks the same standard. If you change model or vendor later, the integration is still yours.",
              )}
            </li>
            <li>
              <span className="font-medium">{translate("Permissions stay in your systems, not in the AI.")}</span>{" "}
              {translate(
                "The AI gets exactly the access your existing roles allow. A salesperson's assistant sees what that salesperson can already see — which is what makes this reviewable under PDPA and PDPC.",
              )}
            </li>
          </ul>
        </ServiceInfoBox>

        <ServiceGrid columns={2}>
          <ServiceFeatureCard
            icon="fas fa-plug"
            title="MCP connectors for your systems"
            description="We build and run the connector between Claude and your ERP, CRM, WhatsApp, warehouse or accounting system — including the audit trail your finance team will ask for."
          />

          <ServiceFeatureCard
            icon="fas fa-user-shield"
            title="Scoped, reviewable access"
            description="Read-only where reading is enough. Write access only where a human approves the action. Every call logged, so you can answer what the AI did and when."
          />

          <ServiceFeatureCard
            icon="fas fa-vials"
            title="Evaluation before go-live"
            description="An AI touching live business systems needs testing against your real data, not a demo. We measure it before it goes near a customer or a ledger."
          />

          <ServiceFeatureCard
            icon="fas fa-route"
            title="Honest scoping"
            description="Plenty of workflows do not need AI at all — a scheduled data sync is cheaper and more reliable. We will tell you when that is the answer."
          />
        </ServiceGrid>

        <p className="mt-8 text-neutral-muted">
          {translate("Working out whether this applies to you? We wrote the long version:")}{" "}
          <Link
            href="/blog/connect-ai-to-business-systems-mcp/"
            className="font-medium text-primary underline underline-offset-4"
          >
            {translate("how AI actually connects to the systems you already run")}
          </Link>
          {translate(" — what MCP means in business terms, what the work costs, and when AI is the wrong answer.")}
        </p>
      </ServiceSection>

      <ServiceSection
        title="Technical Capabilities"
        eyebrow="How we integrate"
        accent="Capabilities"
        dek="Proven technologies and architecture patterns, matched to the way your systems need to talk."
        band="surface"
        className={FULL_BLEED}
      >
        <ServiceInfoBox
          title="Integration Technologies"
          description="Our integration experts leverage a wide range of technologies and approaches, including:"
          variant="neutral"
          className="mb-8"
        >
          <ul className="list-disc pl-5 text-neutral-muted space-y-2">
            <li><span className="font-medium">{translate("RESTful and GraphQL APIs:")}</span> {translate("Modern, scalable API architectures for flexible integration")}</li>
            <li><span className="font-medium">{translate("ESB (Enterprise Service Bus):")}</span> {translate("Centralized integration platform for connecting multiple applications")}</li>
            <li><span className="font-medium">{translate("Middleware Solutions:")}</span> {translate("Message queues, event processing, and data transformation tools")}</li>
            <li><span className="font-medium">{translate("ETL/ELT Processes:")}</span> {translate("Robust data extraction, transformation, and loading pipelines")}</li>
            <li><span className="font-medium">{translate("iPaaS (Integration Platform as a Service):")}</span> {translate("Cloud-based integration for connecting cloud and on-premises applications")}</li>
            <li><span className="font-medium">{translate("WebHooks and Callbacks:")}</span> {translate("Event-driven integration patterns for real-time updates")}</li>
          </ul>
        </ServiceInfoBox>

        {/* Diagram-style view: your systems connected through an API/event-bus spine */}
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-10">
          <h3 className="mb-3 font-display text-xl font-semibold text-neutral-emphasis">
            {translate("Integration Patterns & Architectures")}
          </h3>
          <p className="mb-10 max-w-2xl text-neutral-muted">
            {translate(
              "We implement industry-standard integration patterns tailored to your specific requirements:",
            )}
          </p>

          {/* Mobile: vertical left spine with connector dots; desktop: horizontal spine column */}
          <div className="relative grid grid-cols-1 gap-7 pl-6 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1.25fr)] lg:gap-0 lg:pl-0">
            {/* Vertical spine line — mobile only */}
            <span
              className="absolute bottom-3 left-0 top-3 w-px bg-border lg:hidden"
              aria-hidden
            />

            {/* Connected systems */}
            <div className="flex flex-col justify-center gap-3">
              <p className="mb-1 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-neutral-muted">
                {translate("Your systems")}
              </p>
              {integrationSystems.map((system) => (
                <div key={system} className="relative flex items-center">
                  <span
                    className="absolute -left-6 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-neutral-border bg-card lg:hidden"
                    aria-hidden
                  />
                  <span className="inline-flex rounded-lg border border-border bg-background px-4 py-2 font-mono text-sm text-neutral-emphasis">
                    {translate(system)}
                  </span>
                  <span className="hidden h-px flex-1 bg-border lg:block" aria-hidden />
                </div>
              ))}
            </div>

            {/* Spine — mobile */}
            <div className="relative flex lg:hidden">
              <span
                className="absolute -left-6 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary"
                aria-hidden
              />
              {eventBusLabel}
            </div>

            {/* Spine — desktop */}
            <div className="hidden flex-col items-center lg:flex">
              <span className="w-px flex-1 bg-border" aria-hidden />
              <span className="py-3">{eventBusLabel}</span>
              <span className="w-px flex-1 bg-border" aria-hidden />
            </div>

            {/* Integration patterns */}
            <div className="flex flex-col justify-center gap-7">
              {integrationPatternGroups.map((group) => (
                <div key={group.label} className="relative flex items-start">
                  <span
                    className="absolute -left-6 top-2 h-2 w-2 -translate-x-1/2 rounded-full border border-neutral-border bg-card lg:hidden"
                    aria-hidden
                  />
                  <span
                    className="mt-2 hidden h-px w-8 shrink-0 bg-border lg:block"
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1 lg:pl-5">
                    <p className="mb-2.5 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-neutral-muted">
                      {translate(group.label)}
                    </p>
                    <ul className="space-y-1.5">
                      {group.items.map((item) => (
                        <li
                          key={item}
                          className="flex items-center gap-2.5 text-sm text-neutral-muted"
                        >
                          <span
                            className="h-1 w-1 shrink-0 rounded-full bg-primary"
                            aria-hidden
                          />
                          {translate(item)}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </ServiceSection>

      <ServiceSection
        title="Business Benefits"
        eyebrow="Why it matters"
        accent="Benefits"
        dek="What connected systems unlock — visibility, speed, and lower operating cost."
        band="ivory"
        className={FULL_BLEED}
      >
        <div className="grid grid-cols-1 gap-6">
          <ServiceBenefitItem
            title="Enhanced Data Visibility"
            description="Gain a 360-degree view of your business data across all systems, enabling better decision-making and insights."
          />

          <ServiceBenefitItem
            title="Improved Operational Efficiency"
            description="Eliminate manual data entry and reduce data silos, automating workflows across different applications and departments."
          />

          <ServiceBenefitItem
            title="Accelerated Innovation"
            description="Quickly introduce new applications and technologies into your ecosystem without disrupting existing operations."
          />

          <ServiceBenefitItem
            title="Cost Reduction"
            description="Maximize your technology investments by connecting and leveraging existing systems rather than replacing them."
          />
        </div>
      </ServiceSection>

      <ServiceSection
        title="Our Integration Approach"
        eyebrow="How we deliver"
        accent="Approach"
        dek="A five-step path from assessment to supported, production-grade integrations."
        band="surface"
        className={FULL_BLEED}
      >
        <ServiceStepList steps={integrationApproachSteps} />
      </ServiceSection>

      <ServiceSection
        title="Success Stories"
        eyebrow="Proof"
        accent="Success"
        dek="Real integrations, measured results — for Malaysian education and construction businesses."
        band="ivory"
        className={FULL_BLEED}
      >
        {/* Dark proof moment — measured integration outcomes */}
        <div className="rounded-3xl bg-hero-background p-5 sm:p-8 lg:p-10">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            {successStories.map((story) => (
              <div
                key={story.title}
                className="flex h-full flex-col rounded-2xl border border-primary-contrast/[0.08] bg-primary-contrast/[0.03] p-7"
              >
                <p className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-primary-contrast/40">
                  {translate(story.label)}
                </p>
                <h3 className="mb-7 font-display text-2xl font-semibold text-primary-contrast">
                  {translate(story.title)}
                </h3>
                <div className="mb-7 grid grid-cols-2 gap-x-6 gap-y-6 sm:grid-cols-3">
                  {story.stats.map((stat) => (
                    <div key={stat.label}>
                      <p className="mb-1.5 font-mono text-xs uppercase tracking-[0.15em] text-primary-contrast/50">
                        {translate(stat.label)}
                      </p>
                      <p className="font-display text-3xl font-bold text-primary-contrast sm:text-4xl">
                        {stat.value}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="mt-auto text-sm leading-relaxed text-primary-contrast/70">
                  {translate(story.narrative)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </ServiceSection>

      {/* FAQ Section for rich snippets */}
      <ServiceSection
        title="Frequently Asked Questions"
        eyebrow="FAQ"
        accent="Questions"
        dek="Common questions about system integration for Malaysian businesses."
        band="surface"
        className={FULL_BLEED}
      >
        <ServiceFaq items={sisFAQs} />
      </ServiceSection>
      <StructuredData type="faq" faqs={sisFAQs} />

      <ConsultationCTA
        title="Ready to Connect Your Business Systems?"
        description="Let's discuss how our system integration expertise can help streamline your operations, eliminate data silos, and create a more connected, efficient business."
        buttonText="Request a Consultation"
      />
    </ServicePageLayout>
  );
}
